import { EventCardTemplate } from "./components/EventCardTemplate";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useCustomMutation } from "@refinedev/core";
import {
  Button,
  Card,
  DatePicker,
  Descriptions,
  Divider,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Col,
  Select,
  Space,
  Steps,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from "antd";
import {
  CheckOutlined,
  DeleteOutlined,
  LeftOutlined,
  PlusOutlined,
  RightOutlined,
} from "@ant-design/icons";
import { apiUrl } from "@/consts";
import UserSearchSelect from "@/pages/components/UserSearchSelect";
import ClubTeamSelect, { type ClubTeamValue } from "./components/ClubTeamSelect";
import AddressSelect, { type AddressDTO } from "./components/AddressSelect";

const { Title, Text } = Typography;

// ─── Types ────────────────────────────────────────────────────────────────────

type ScoutingEvent = {
  id: string;
  title: string;
  location: string;
  startTime: string;
  description: string | null;
  status: string;
};

type EventEmployee = {
  id: string;
  eventId: string;
  allowedMetrics: string[];
  canLockScore: boolean;
  employee: { id: string; username: string; fullName?: string | null; email: string };
};

const ALL_METRICS = [
  "power",
  "acceleration",
  "speed",
  "accuracy",
  "agility",
  "dribble",
] as const;

// ─── Step 1: Event Details ────────────────────────────────────────────────────

function EventDetailsStep({
  onNext,
}: {
  onNext: (event: ScoutingEvent) => void;
}) {
  const [form] = Form.useForm();
  const [templateBusy, setTemplateBusy] = useState(false);
  const { mutate, isLoading } = useCustomMutation<{ event: ScoutingEvent }>();
  const [clubTeam, setClubTeam] = useState<ClubTeamValue>({
    clubId: null,
    teamId: null,
  });
  const [venue, setVenue] = useState<AddressDTO | null>(null);

  function handleFinish(values: any) {
    if (templateBusy) { message.warning("Wait for the player card preview to finish"); return; }
    const priceEuros = values.ticketPriceEuros;
    const ticketPriceCents =
      priceEuros != null && priceEuros !== ""
        ? Math.round(Number(priceEuros) * 100)
        : null;

    if (!venue) {
      message.error("Please select a venue address");
      return;
    }

    mutate(
      {
        url: `${apiUrl}/scouting-event`,
        method: "post",
        values: {
          playerCardTemplateKey: values.playerCardTemplateKey ?? null,
          title: values.title,
          address: venue,
          startTime: values.startTime?.toISOString(),
          description: values.description || null,
          onboardingHeadline: values.onboardingHeadline || null,
          onboardingBlurb: values.onboardingBlurb || null,
          promoVideoUrl:
            typeof values.promoVideoUrl === "string" && values.promoVideoUrl.trim()
              ? values.promoVideoUrl.trim()
              : null,
          ticketEnabled: !!values.ticketEnabled,
          publicRegistrationEnabled: !!values.publicRegistrationEnabled,
          ticketLabel: values.ticketLabel || null,
          ticketDescription: values.ticketDescription || null,
          ticketPriceCents,
          ticketCurrency: values.ticketCurrency ?? "eur",
          clubId: clubTeam.clubId,
          teamId: clubTeam.teamId,
        },
      },
      {
        onSuccess: (data) => {
          onNext(data.data.event);
        },
        onError: (err: any) =>
          message.error(err?.message ?? "Failed to create event"),
      }
    );
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={{ ticketCurrency: "eur", ticketEnabled: false }}
    >
      <Form.Item
        label="Title"
        name="title"
        rules={[{ required: true, message: "Title is required" }]}
      >
        <Input placeholder="U16 Regional Scouting Day" />
      </Form.Item>

      <Form.Item label="Location" required help="Search and pick the venue address.">
        <AddressSelect id="scoutingVenue" value={venue} onChange={setVenue} />
      </Form.Item>

      <Form.Item
        label="Start Date & Time"
        name="startTime"
        rules={[{ required: true, message: "Start time is required" }]}
      >
        <DatePicker showTime style={{ width: "100%" }} />
      </Form.Item>

      <Form.Item label="Description" name="description">
        <Input.TextArea rows={3} placeholder="Any notes about this event…" />
      </Form.Item>

      <Divider orientation="left">Player card template</Divider>
      <Form.Item name="playerCardTemplateKey">
        <EventCardTemplate onBusyChange={setTemplateBusy} />
      </Form.Item>

      <Divider orientation="left">Club & team</Divider>
      <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        Optionally attach this event to a club and one of its teams. Choose a club to
        enable the team select.
      </Text>
      <ClubTeamSelect
        value={clubTeam}
        onChange={(next) => {
          setClubTeam(next);
          if (!next.clubId) form.setFieldValue("publicRegistrationEnabled", false);
        }}
        style={{ marginBottom: 24 }}
      />

      <Form.Item
        name="publicRegistrationEnabled"
        label="Public club testing registration"
        valuePropName="checked"
        extra="Anyone can register without a club membership. Active upcoming events appear on the club website. Enable tickets below to include checkout."
      >
        <Switch disabled={!clubTeam.clubId} />
      </Form.Item>
      <Divider orientation="left">Tickets & onboarding</Divider>
      <Text type="secondary" style={{ display: "block", marginBottom: 16 }}>
        Set a price to auto-provision a Stripe product and price for ticket sales.
        Stripe IDs are written back to the event.
      </Text>

      <Row gutter={12}>
        <Col xs={24} sm={12}>
          <Form.Item
            label="Ticket sales enabled"
            name="ticketEnabled"
            valuePropName="checked"
          >
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={12}>
        <Col xs={24} sm={12}>
          <Form.Item label="Ticket label" name="ticketLabel">
            <Input placeholder="Scout Trial Ticket — Dublin" />
          </Form.Item>
        </Col>
        <Col xs={24} sm={6}>
          <Form.Item label="Currency" name="ticketCurrency">
            <Select
              options={[
                { value: "eur", label: "EUR" },
                { value: "usd", label: "USD" },
                { value: "gbp", label: "GBP" },
              ]}
            />
          </Form.Item>
        </Col>
        <Col xs={24} sm={6}>
          <Form.Item label="Price" name="ticketPriceEuros">
            <InputNumber
              min={0}
              step={0.01}
              style={{ width: "100%" }}
              placeholder="29.00"
            />
          </Form.Item>
        </Col>
      </Row>

      <Form.Item label="Ticket description" name="ticketDescription">
        <Input.TextArea rows={2} placeholder="Shown on the ticket page and in Stripe" />
      </Form.Item>

      <Form.Item label="Onboarding headline" name="onboardingHeadline">
        <Input placeholder="Hero copy on the event landing page" />
      </Form.Item>

      <Form.Item label="Onboarding blurb" name="onboardingBlurb">
        <Input.TextArea rows={3} placeholder="Supporting paragraph below the headline" />
      </Form.Item>

      <Form.Item
        label="Promo video URL"
        name="promoVideoUrl"
        extra="YouTube or Vimeo link shown as the hero on the event landing page."
        rules={[{ type: "url", message: "Must be a valid URL" }]}
      >
        <Input placeholder="https://www.youtube.com/watch?v=..." />
      </Form.Item>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <Button
          type="primary"
          htmlType="submit"
          loading={isLoading}
          disabled={templateBusy}
          icon={<RightOutlined />}
          iconPosition="end"
        >
          Next
        </Button>
      </div>
    </Form>
  );
}

// ─── Step 2: Assign Employees ─────────────────────────────────────────────────

function AssignEmployeesStep({
  eventId,
  employees,
  onEmployeesChange,
  onNext,
  onBack,
}: {
  eventId: string;
  employees: EventEmployee[];
  onEmployeesChange: (employees: EventEmployee[]) => void;
  onNext: () => void;
  onBack: () => void;
}) {
  const { mutate: assignMutate } = useCustomMutation();
  const { mutate: removeMutate } = useCustomMutation();
  const { mutate: inviteMutate } = useCustomMutation();

  const [assignState, setAssignState] = useState<{
    userId: string;
    metrics: string[];
    canLockScore: boolean;
  }>({ userId: "", metrics: [], canLockScore: false });
  const [assigning, setAssigning] = useState(false);
  const [removingEmployee, setRemovingEmployee] = useState<Record<string, boolean>>({});
  const [inviteModal, setInviteModal] = useState<{
    open: boolean;
    email: string;
    fullName: string;
  }>({ open: false, email: "", fullName: "" });
  const [invitingEmployee, setInvitingEmployee] = useState(false);

  async function refreshEmployees() {
    try {
      const token = localStorage.getItem("refine-auth");
      const res = await fetch(`${apiUrl}/scouting-event/${eventId}/employees`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      onEmployeesChange(data.employees ?? []);
    } catch {
      // silent
    }
  }

  function handleAssign() {
    if (!assignState.userId) {
      message.error("Select a user first");
      return;
    }
    setAssigning(true);
    assignMutate(
      {
        url: `${apiUrl}/scouting-event/${eventId}/employees`,
        method: "post",
        values: {
          employeeUserId: assignState.userId,
          allowedMetrics: assignState.metrics.length > 0 ? assignState.metrics : undefined,
          canLockScore: assignState.canLockScore ?? false,
        },
      },
      {
        onSuccess: () => {
          message.success("Employee assigned");
          setAssignState({ userId: "", metrics: [], canLockScore: false });
          setAssigning(false);
          refreshEmployees();
        },
        onError: (err: any) => {
          message.error(err?.message ?? "Failed to assign employee");
          setAssigning(false);
        },
      }
    );
  }

  function handleRemove(employeeUserId: string) {
    setRemovingEmployee((prev) => ({ ...prev, [employeeUserId]: true }));
    removeMutate(
      {
        url: `${apiUrl}/scouting-event/${eventId}/employees/${employeeUserId}`,
        method: "delete",
        values: {},
      },
      {
        onSuccess: () => {
          message.success("Employee removed");
          setRemovingEmployee((prev) => ({ ...prev, [employeeUserId]: false }));
          refreshEmployees();
        },
        onError: (err: any) => {
          message.error(err?.message ?? "Failed to remove employee");
          setRemovingEmployee((prev) => ({ ...prev, [employeeUserId]: false }));
        },
      }
    );
  }

  function openInviteModal(seed: string) {
    setInviteModal({
      open: true,
      email: seed.includes("@") ? seed : "",
      fullName: "",
    });
  }

  function handleInviteAndAssign() {
    if (!inviteModal.email.trim() || !inviteModal.fullName.trim()) {
      message.error("Please provide employee name and email");
      return;
    }
    setInvitingEmployee(true);
    inviteMutate(
      {
        url: `${apiUrl}/scouting-event/${eventId}/employees/invite`,
        method: "post",
        values: {
          email: inviteModal.email.trim(),
          name: inviteModal.fullName.trim(),
        },
      },
      {
        onSuccess: (inviteRes: any) => {
          const employeeUserId = inviteRes?.data?.userId;
          if (!employeeUserId) {
            message.error("Invitation created but user id missing");
            setInvitingEmployee(false);
            return;
          }
          assignMutate(
            {
              url: `${apiUrl}/scouting-event/${eventId}/employees`,
              method: "post",
              values: {
                employeeUserId,
                allowedMetrics: assignState.metrics.length ? assignState.metrics : undefined,
                canLockScore: assignState.canLockScore ?? false,
              },
            },
            {
              onSuccess: () => {
                message.success("Employee invited and assigned");
                setAssignState({ userId: "", metrics: [], canLockScore: false });
                setInviteModal({ open: false, email: "", fullName: "" });
                setInvitingEmployee(false);
                refreshEmployees();
              },
              onError: (err: any) => {
                message.error(err?.message ?? "Employee invited but assignment failed");
                setInvitingEmployee(false);
              },
            }
          );
        },
        onError: (err: any) => {
          message.error(err?.message ?? "Failed to invite employee");
          setInvitingEmployee(false);
        },
      }
    );
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      {/* Existing assignments */}
      {employees.length > 0 && (
        <Table
          size="small"
          dataSource={employees}
          rowKey="id"
          pagination={false}
          style={{ marginBottom: 8 }}
          columns={[
            {
              title: "Full Name",
              render: (_: any, emp: EventEmployee) =>
                emp.employee.fullName || emp.employee.username || emp.employee.email,
            },
            { title: "Email", dataIndex: ["employee", "email"] },
            {
              title: "Allowed Metrics",
              dataIndex: "allowedMetrics",
              render: (metrics: string[]) => (
                <Space wrap size={[4, 4]}>
                  {metrics.map((m) => <Tag key={m}>{m}</Tag>)}
                </Space>
              ),
            },
            {
              title: "Can Lock Score",
              dataIndex: "canLockScore",
              render: (val: boolean) =>
                val ? <Tag color="orange">Yes</Tag> : <Tag>No</Tag>,
            },
            {
              title: "Actions",
              width: 80,
              render: (_: any, emp: EventEmployee) => (
                <Popconfirm
                  title="Remove this employee?"
                  onConfirm={() => handleRemove(emp.employee.id)}
                  okText="Remove"
                  cancelText="Cancel"
                >
                  <Button
                    danger
                    size="small"
                    icon={<DeleteOutlined />}
                    loading={removingEmployee[emp.employee.id] ?? false}
                  />
                </Popconfirm>
              ),
            },
          ]}
        />
      )}

      {/* Assign form */}
      <Card size="small" title="Assign Employee">
        <Form layout="vertical">
          <Row gutter={12} align="bottom">
            <Col xs={24} sm={9}>
              <Form.Item label="Employee" style={{ marginBottom: 8 }}>
                <UserSearchSelect
                  style={{ minWidth: 420 }}
                  userType={["ADMIN", "SUPER_ADMIN", "EMPLOYEE", "USER"]}
                  onInviteRequested={(query) => openInviteModal(query)}
                  onChange={(userId) =>
                    setAssignState((prev) => ({ ...prev, userId }))
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={9}>
              <Form.Item label="Allowed Metrics" style={{ marginBottom: 8 }}>
                <Select
                  mode="multiple"
                  style={{ width: "100%" }}
                  placeholder="All metrics (default)"
                  value={assignState.metrics}
                  onChange={(metrics) =>
                    setAssignState((prev) => ({ ...prev, metrics }))
                  }
                  options={ALL_METRICS.map((m) => ({ value: m, label: m }))}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={3}>
              <Form.Item label="Can Lock Score" style={{ marginBottom: 8 }}>
                <Switch
                  size="small"
                  checked={assignState.canLockScore}
                  onChange={(checked) =>
                    setAssignState((prev) => ({ ...prev, canLockScore: checked }))
                  }
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={3}>
              <Form.Item label=" " style={{ marginBottom: 8, marginTop: 8 }}>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleAssign}
                  disabled={!assignState.userId}
                  loading={assigning}
                  style={{ width: "100%" }}
                >
                  Assign
                </Button>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Card>

      <Modal
        title="Invite Employee"
        open={inviteModal.open}
        confirmLoading={invitingEmployee}
        okText="Invite and Assign"
        onOk={handleInviteAndAssign}
        onCancel={() => {
          if (invitingEmployee) return;
          setInviteModal({ open: false, email: "", fullName: "" });
        }}
      >
        <Space direction="vertical" style={{ width: "100%" }} size={12}>
          <Input
            placeholder="Full name"
            value={inviteModal.fullName}
            onChange={(e) => setInviteModal((prev) => ({ ...prev, fullName: e.target.value }))}
          />
          <Input
            placeholder="Email"
            value={inviteModal.email}
            onChange={(e) => setInviteModal((prev) => ({ ...prev, email: e.target.value }))}
          />
        </Space>
      </Modal>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Button icon={<LeftOutlined />} onClick={onBack}>
          Back
        </Button>
        <Button
          type="primary"
          onClick={onNext}
          icon={<RightOutlined />}
          iconPosition="end"
        >
          Next
        </Button>
      </div>
    </div>
  );
}

// ─── Step 3: Confirm ──────────────────────────────────────────────────────────

function ConfirmStep({
  event,
  employees,
  onBack,
  onDone,
  finishing,
}: {
  event: ScoutingEvent;
  employees: EventEmployee[];
  onBack: () => void;
  onDone: (status: string) => void;
  finishing: boolean;
}) {
  const [selectedStatus, setSelectedStatus] = useState(event.status);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>
      <Card title="Event">
        <Descriptions bordered column={2} size="small">
          <Descriptions.Item label="Title">{event.title}</Descriptions.Item>
          <Descriptions.Item label="Location">{event.location}</Descriptions.Item>
          <Descriptions.Item label="Start Time">
            {new Date(event.startTime).toLocaleString()}
          </Descriptions.Item>
          <Descriptions.Item label="Status">
            <Select
              value={selectedStatus}
              onChange={setSelectedStatus}
              style={{ width: 140 }}
              options={[
                { value: "DRAFT", label: "DRAFT" },
                { value: "ACTIVE", label: "ACTIVE" },
                { value: "COMPLETED", label: "COMPLETED" },
                { value: "CANCELLED", label: "CANCELLED" },
              ]}
            />
          </Descriptions.Item>
          {event.description && (
            <Descriptions.Item label="Description" span={2}>
              {event.description}
            </Descriptions.Item>
          )}
        </Descriptions>
      </Card>

      <Card title={`Employees (${employees.length})`}>
        {employees.length === 0 ? (
          <Text type="secondary">No employees assigned</Text>
        ) : (
          <Space direction="vertical" style={{ width: "100%" }}>
            {employees.map((emp) => (
              <div key={emp.id}>
                <Text strong>
                  {emp.employee.fullName || emp.employee.username || emp.employee.email}
                </Text>
                <Text type="secondary"> — {emp.employee.email}</Text>
                <div style={{ marginTop: 4 }}>
                  {emp.allowedMetrics.map((m) => (
                    <Tag key={m}>{m}</Tag>
                  ))}
                </div>
              </div>
            ))}
          </Space>
        )}
      </Card>

      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Button icon={<LeftOutlined />} onClick={onBack}>
          Back
        </Button>
        <Button
          type="primary"
          icon={<CheckOutlined />}
          onClick={() => onDone(selectedStatus)}
          loading={finishing}
        >
          {selectedStatus !== event.status ? `Set to ${selectedStatus} & Finish` : "Finish"}
        </Button>
      </div>
    </div>
  );
}

// ─── Main page ────────────────────────────────────────────────────────────────

export function ScoutingEventCreate() {
  const navigate = useNavigate();
  const { mutate } = useCustomMutation();

  const [step, setStep] = useState(0);
  const [createdEvent, setCreatedEvent] = useState<ScoutingEvent | null>(null);
  const [employees, setEmployees] = useState<EventEmployee[]>([]);
  const [finishing, setFinishing] = useState(false);

  function handleEventCreated(event: ScoutingEvent) {
    setCreatedEvent(event);
    setStep(1);
  }

  function handleDone(status: string) {
    const id = createdEvent!.id;
    if (status !== createdEvent!.status) {
      setFinishing(true);
      mutate(
        { url: `${apiUrl}/scouting-event/${id}`, method: "put", values: { status } },
        {
          onSuccess: () => navigate(`/scouting-events/${id}`),
          onError: (err: any) => {
            message.error(err?.message ?? "Failed to update status");
            setFinishing(false);
          },
        }
      );
    } else {
      navigate(`/scouting-events/${id}`);
    }
  }

  const STEPS = [
    { title: "Event Details" },
    { title: "Assign Employees" },
    { title: "Confirm" },
  ];

  return (
    <div style={{ padding: "0 8px" }}>
      <Title level={3} style={{ marginBottom: 8 }}>
        Create Scouting Event
      </Title>
      <Text type="secondary" style={{ display: "block", marginBottom: 24 }}>
        New events start in DRAFT — you can edit them before going live.
      </Text>

      <Steps
        current={step}
        items={STEPS}
        style={{ marginBottom: 32 }}
      />

      <Card>
        {step === 0 && (
          <EventDetailsStep onNext={handleEventCreated} />
        )}

        {step === 1 && createdEvent && (
          <AssignEmployeesStep
            eventId={createdEvent.id}
            employees={employees}
            onEmployeesChange={setEmployees}
            onNext={() => setStep(2)}
            onBack={() => setStep(0)}
          />
        )}

        {step === 2 && createdEvent && (
          <ConfirmStep
            event={createdEvent}
            employees={employees}
            onBack={() => setStep(1)}
            onDone={(status) => handleDone(status)}
            finishing={finishing}
          />
        )}
      </Card>
    </div>
  );
}
